// LT-379 — Calling tab on Settings hub. Lets the user pick a default
// Agent for their outbound calls. Persists onto
// UserAssistantConfiguration.agent_id; null/blank means "fall back to
// workspace default" at dispatch time (the resolution chain is owned
// by the backend dispatcher introduced in LT-340 / LT-341).
//
// Tab is visible to every authenticated user — the Agent catalogue
// itself is admin-only (see SETTINGS_SECTIONS), but selecting a personal
// default from the published catalogue is not.
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select } from '../../ui';
import { agentsService } from '../../services/agentsService';
import {
  userAssistantConfigService,
  type UserAssistantConfig,
} from '../../services/userAssistantConfigService';
import type { Agent } from '../../types/agent';
import {
  extractErrorMessage,
  workspaceAPI,
  type WorkspaceDefaults,
} from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { useAuth } from '../../context/AuthContext';

const WORKSPACE_DEFAULT_VALUE = '';

const WEEKDAYS: { value: string; label: string }[] = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const SettingsCallingTab: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [config, setConfig] = useState<UserAssistantConfig | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string>(WORKSPACE_DEFAULT_VALUE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Catalogue + saved selection in parallel — both feed the
        // initial render and neither depends on the other.
        const [agentRows, savedConfig] = await Promise.all([
          agentsService.list({ includeSystem: true, includeDeprecated: false }),
          userAssistantConfigService.getConfig().catch(() => null),
        ]);
        if (cancelled) return;
        setAgents(agentRows);
        setConfig(savedConfig);
        setSelectedAgentId(savedConfig?.agent_id ?? WORKSPACE_DEFAULT_VALUE);
      } catch (err: unknown) {
        if (cancelled) return;
        setMessage({
          type: 'error',
          text: extractErrorMessage(err, 'Failed to load Agents'),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-clear success toasts so the page doesn't stay green forever.
  useEffect(() => {
    if (message?.type === 'success') {
      const t = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  // Group catalogue rows by category for the optgroups. Stable order
  // within each group: name asc.
  const grouped = useMemo(() => {
    const byCat = new Map<string, Agent[]>();
    for (const a of agents) {
      const cat = a.category || 'other';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(a);
    }
    byCat.forEach((list) => list.sort((x: Agent, y: Agent) => x.name.localeCompare(y.name)));
    return Array.from(byCat.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [agents]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    // Empty string in the dropdown → null on the wire (clear back to
    // workspace default). Anything else is a UUID.
    const agentIdPayload: string | null =
      selectedAgentId === WORKSPACE_DEFAULT_VALUE ? null : selectedAgentId;
    try {
      const updated = config
        ? await userAssistantConfigService.updateConfig({ agent_id: agentIdPayload })
        : await userAssistantConfigService.createConfig({ agent_id: agentIdPayload });
      setConfig(updated);
      setMessage({ type: 'success', text: 'Default Agent updated.' });
      track(AnalyticsEvents.SETTINGS_DEFAULT_AGENT_SAVED, {
        agent_id: agentIdPayload,
      });
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: extractErrorMessage(err, 'Failed to save default Agent'),
      });
    } finally {
      setSaving(false);
    }
  };

  const dirty = (config?.agent_id ?? WORKSPACE_DEFAULT_VALUE) !== selectedAgentId;

  return (
    <div className="max-w-2xl space-y-4">
      {message ? (
        <div
          className={`rounded-ds-lg px-3 py-2 text-sm border ${
            message.type === 'success'
              ? 'bg-ok-tint border-ok-border text-ok-text'
              : 'bg-danger-tint border-danger-border text-danger-text'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      <Select
        id="settings-calling-agent"
        label="Default Agent"
        hint="Used for new outbound calls when no campaign-level Agent is set. Leave on workspace default to inherit the admin-configured choice."
        value={selectedAgentId}
        onChange={(e) => setSelectedAgentId(e.target.value)}
        disabled={loading || saving}
      >
        <option value={WORKSPACE_DEFAULT_VALUE}>(Use workspace default)</option>
        {grouped.map(([cat, rows]) => (
          <optgroup key={cat} label={cat.replace(/_/g, ' ')}>
            {rows.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.is_system ? ' (system)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>

      <div className="pt-2">
        <Button
          intent="primary"
          onClick={handleSave}
          disabled={loading || saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <CampaignDefaultsSection />
    </div>
  );
};

// ---------------------------------------------------------------------------
// LT-546 — Default campaign settings. Workspace-level defaults that both
// campaign-creation paths (wizard + upload/brief) inherit when the operator
// doesn't override per-campaign. Admin-only write (backend 403s members);
// non-admins see a read-only view.
// ---------------------------------------------------------------------------

const VOICE_GENDERS: { value: string; label: string }[] = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
];

const VOICE_LANGUAGES: { value: string; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'en_hi', label: 'English + Hindi' },
  { value: 'en_id', label: 'English + Bahasa' },
];

const CampaignDefaultsSection: React.FC = () => {
  const { user } = useAuth();
  const isWorkspaceAdmin = (user?.role ?? '').toLowerCase() === 'admin';

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<WorkspaceDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // retry_delays_days is edited as a comma-separated string so partial input
  // (e.g. mid-typing "1, 2,") doesn't fight the controlled number array.
  const [delaysText, setDelaysText] = useState('');
  // LT-554 — workspace default Agent (distinct from the per-user default
  // above; this is the workspace-wide default both campaign-creation paths
  // offer as the zero-effort choice).
  const [agents, setAgents] = useState<Agent[]>([]);
  const [defaultAgentId, setDefaultAgentId] = useState<string>('');
  const [savedDefaultAgentId, setSavedDefaultAgentId] = useState<string>('');
  const [savingAgent, setSavingAgent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const ws = await workspaceAPI.getWorkspace();
        const [d, agentRows, da] = await Promise.all([
          workspaceAPI.getDefaults(ws.id),
          agentsService.list({ includeSystem: true, includeDeprecated: false }).catch(() => [] as Agent[]),
          workspaceAPI.getDefaultAgent(ws.id).catch(() => ({ default_agent_id: null })),
        ]);
        if (cancelled) return;
        setWorkspaceId(ws.id);
        setDefaults(d);
        setDelaysText((d.retry_policy_default?.retry_delays_days ?? []).join(', '));
        setAgents(agentRows);
        setDefaultAgentId(da.default_agent_id ?? '');
        setSavedDefaultAgentId(da.default_agent_id ?? '');
      } catch (err: unknown) {
        if (cancelled) return;
        setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to load campaign defaults') });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveDefaultAgent = async () => {
    if (!workspaceId || !defaultAgentId) return;
    setSavingAgent(true);
    setMessage(null);
    try {
      const res = await workspaceAPI.updateDefaultAgent(workspaceId, defaultAgentId);
      setSavedDefaultAgentId(res.default_agent_id ?? '');
      setDefaultAgentId(res.default_agent_id ?? '');
      setMessage({ type: 'success', text: 'Default agent updated.' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to save default agent') });
    } finally {
      setSavingAgent(false);
    }
  };

  useEffect(() => {
    if (message?.type === 'success') {
      const t = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const patch = (next: Partial<WorkspaceDefaults>) =>
    setDefaults((prev) => (prev ? { ...prev, ...next } : prev));

  const patchDialWindow = (next: Partial<NonNullable<WorkspaceDefaults['dial_window_default']>>) =>
    setDefaults((prev) => {
      if (!prev) return prev;
      const dw = prev.dial_window_default ?? { start: '10:00', end: '17:00', timezone: '', days: [] };
      return { ...prev, dial_window_default: { ...dw, ...next } };
    });

  const toggleDay = (day: string) => {
    const dw = defaults?.dial_window_default;
    const current = dw?.days ?? [];
    const nextDays = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    patchDialWindow({ days: nextDays });
  };

  const handleSave = async () => {
    if (!workspaceId || !defaults) return;
    setSaving(true);
    setMessage(null);
    // Parse the comma-separated delays into positive ints for the wire body.
    const parsedDelays = delaysText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => Number(s));
    const retryCount = defaults.retry_policy_default?.retry_count ?? parsedDelays.length;
    try {
      const updated = await workspaceAPI.updateDefaults(workspaceId, {
        voice_gender_default: defaults.voice_gender_default,
        voice_language_default: defaults.voice_language_default,
        dial_window_default: defaults.dial_window_default,
        retry_policy_default: { retry_count: retryCount, retry_delays_days: parsedDelays },
      });
      setDefaults(updated);
      setDelaysText((updated.retry_policy_default?.retry_delays_days ?? []).join(', '));
      setMessage({ type: 'success', text: 'Default campaign settings updated.' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to save campaign defaults') });
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving || !isWorkspaceAdmin || !defaults;
  const dw = defaults?.dial_window_default;

  return (
    <section className="mt-8 border-t border-white/10 pt-6 space-y-4">
      <div>
        <h2 className="text-base font-medium text-white">Default campaign settings</h2>
        <p className="text-sm text-slate-400">
          New campaigns inherit these when you don&apos;t set them per-campaign. Used by both the
          wizard and the upload-a-brief flow.
        </p>
      </div>

      {message ? (
        <div
          className={`rounded-ds-lg px-3 py-2 text-sm border ${
            message.type === 'success'
              ? 'bg-ok-tint border-ok-border text-ok-text'
              : 'bg-danger-tint border-danger-border text-danger-text'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      {!isWorkspaceAdmin ? (
        <p className="text-sm text-warn-text">Only workspace admins can change these defaults.</p>
      ) : null}

      {/* LT-554 — workspace default Agent. Both creation paths offer this as
          the "use workspace default agent" choice. Saved via its own
          endpoint, so it has a dedicated Save button. */}
      <div className="space-y-2">
        <Select
          id="ws-default-agent"
          label="Default agent"
          hint="The agent both the wizard and upload paths offer as the default for new campaigns."
          value={defaultAgentId}
          onChange={(e) => setDefaultAgentId(e.target.value)}
          disabled={loading || savingAgent || !isWorkspaceAdmin || agents.length === 0}
        >
          <option value="">{agents.length === 0 ? '(no agents yet)' : 'Select an agent…'}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.is_system ? ' (system)' : ''}
            </option>
          ))}
        </Select>
        <Button
          intent="secondary"
          onClick={handleSaveDefaultAgent}
          disabled={
            loading || savingAgent || !isWorkspaceAdmin || !defaultAgentId ||
            defaultAgentId === savedDefaultAgentId
          }
        >
          {savingAgent ? 'Saving…' : 'Save default agent'}
        </Button>
      </div>

      <Select
        id="ws-default-voice-gender"
        label="Default voice gender"
        value={defaults?.voice_gender_default ?? ''}
        onChange={(e) => patch({ voice_gender_default: (e.target.value || null) as WorkspaceDefaults['voice_gender_default'] })}
        disabled={disabled}
      >
        {VOICE_GENDERS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>

      <Select
        id="ws-default-voice-language"
        label="Default voice language"
        value={defaults?.voice_language_default ?? ''}
        onChange={(e) => patch({ voice_language_default: (e.target.value || null) as WorkspaceDefaults['voice_language_default'] })}
        disabled={disabled}
      >
        {VOICE_LANGUAGES.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>

      <div className="grid grid-cols-2 gap-4">
        <Input
          id="ws-dial-start"
          type="time"
          label="Dial window start"
          value={dw?.start ?? ''}
          onChange={(e) => patchDialWindow({ start: e.target.value })}
          disabled={disabled}
        />
        <Input
          id="ws-dial-end"
          type="time"
          label="Dial window end"
          value={dw?.end ?? ''}
          onChange={(e) => patchDialWindow({ end: e.target.value })}
          disabled={disabled}
        />
      </div>

      <Input
        id="ws-dial-timezone"
        label="Dial window timezone"
        hint="IANA timezone, e.g. Asia/Kolkata or Australia/Brisbane."
        value={dw?.timezone ?? ''}
        onChange={(e) => patchDialWindow({ timezone: e.target.value })}
        disabled={disabled}
      />

      <div>
        <span className="block text-sm text-slate-300 mb-1">Dial days</span>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((d) => {
            const active = (dw?.days ?? []).includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                disabled={disabled}
                className={`px-3 py-1 rounded-ds-lg text-sm border transition-colors ${
                  active
                    ? 'bg-accent-tint border-accent-border text-accent'
                    : 'bg-surface-1 border-white/10 text-slate-400'
                } disabled:opacity-50`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          id="ws-retry-count"
          type="number"
          min={1}
          max={5}
          label="Retry count"
          hint="Retries after the first attempt (1–5)."
          value={defaults?.retry_policy_default?.retry_count ?? ''}
          onChange={(e) =>
            patch({
              retry_policy_default: {
                retry_count: Number(e.target.value),
                retry_delays_days: defaults?.retry_policy_default?.retry_delays_days ?? [],
              },
            })
          }
          disabled={disabled}
        />
        <Input
          id="ws-retry-delays"
          label="Retry delays (days)"
          hint="Comma-separated; one per retry."
          value={delaysText}
          onChange={(e) => setDelaysText(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="pt-2">
        <Button intent="primary" onClick={handleSave} disabled={disabled}>
          {saving ? 'Saving…' : 'Save defaults'}
        </Button>
      </div>
    </section>
  );
};

export default SettingsCallingTab;
